import { useState } from "react";
import { motion } from "framer-motion";
import { useLocation, Link, useSearchParams } from "react-router-dom";
import { Shield, Lock, ArrowRight, Unlock, Mail, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { API } from "@/lib/api";

export default function LoginPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const fromPath = location.state?.from;
  const error = searchParams.get("error");

  const [email, setEmail] = useState("");
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState("");

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      setMagicLinkError("Please enter a valid email address");
      return;
    }
    setMagicLinkLoading(true);
    setMagicLinkError("");
    try {
      await axios.post(`${API}/auth/magic-link/request`, { email: email.trim().toLowerCase() });
      setMagicLinkSent(true);
    } catch (err) {
      setMagicLinkError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setMagicLinkLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-900/20 via-zinc-900 to-zinc-950" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <Card className="bg-zinc-900/80 border-zinc-800 backdrop-blur-sm">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-emerald-500" />
            </div>
            <CardTitle className="text-2xl font-bold text-white">
              Premium Tool Access
            </CardTitle>
            <CardDescription className="text-zinc-400">
              {fromPath ? "Sign in to access this premium tool" : "Sign in to access premium SEO tools"}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-5 pt-4">
            {fromPath && (
              <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <p className="text-sm text-amber-400 text-center">
                  <Lock className="w-4 h-4 inline mr-2" />
                  This tool requires a premium account
                </p>
              </div>
            )}

            {error === "invalid_link" && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                <p className="text-sm text-red-400">That link is invalid or has already been used. Please request a new one.</p>
              </div>
            )}
            {error === "link_expired" && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                <p className="text-sm text-red-400">That link has expired. Please request a new one below.</p>
              </div>
            )}

            {/* Google Login */}
            <Button
              onClick={handleGoogleLogin}
              className="w-full h-12 bg-white hover:bg-zinc-100 text-zinc-900 font-medium"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </Button>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-zinc-800" />
              <span className="text-xs text-zinc-500 uppercase tracking-wider">or</span>
              <div className="flex-1 h-px bg-zinc-800" />
            </div>

            {/* Magic Link */}
            {magicLinkSent ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-center space-y-2"
                data-testid="magic-link-sent"
              >
                <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto" />
                <p className="text-sm text-emerald-300 font-medium">Check your email</p>
                <p className="text-xs text-zinc-400">
                  We sent a sign-in link to <span className="text-white font-medium">{email}</span>. It expires in 15 minutes.
                </p>
                <button
                  onClick={() => { setMagicLinkSent(false); setEmail(""); }}
                  className="text-xs text-emerald-500 hover:text-emerald-400 underline mt-2"
                  data-testid="magic-link-try-again"
                >
                  Use a different email
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleMagicLink} className="space-y-3" data-testid="magic-link-form">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setMagicLinkError(""); }}
                    className="pl-10 h-12 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                    data-testid="magic-link-email-input"
                    required
                  />
                </div>
                {magicLinkError && (
                  <p className="text-xs text-red-400" data-testid="magic-link-error">{magicLinkError}</p>
                )}
                <Button
                  type="submit"
                  disabled={magicLinkLoading}
                  className="w-full h-11 bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700"
                  data-testid="magic-link-submit-btn"
                >
                  {magicLinkLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  Sign in with Email
                </Button>
              </form>
            )}

            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm text-zinc-400">
                <Shield className="w-4 h-4 text-emerald-500" />
                <span>No password required</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-zinc-400">
                <ArrowRight className="w-4 h-4 text-emerald-500" />
                <span>Access all SEO tools instantly</span>
              </div>
            </div>

            <p className="text-xs text-zinc-500 text-center pt-4 border-t border-zinc-800">
              By signing in, you agree to our{" "}
              <Link to="/terms" className="text-zinc-400 hover:text-white underline">Terms of Service</Link> and{" "}
              <Link to="/privacy" className="text-zinc-400 hover:text-white underline">Privacy Policy</Link>
            </p>

            <div className="text-center pt-4">
              <Link to="/" className="inline-flex items-center gap-2 text-sm text-emerald-500 hover:text-emerald-400">
                <Unlock className="w-4 h-4" />
                Browse free tools without signing in
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-zinc-600 text-sm mt-6">
          Powered by Emerald Consulting
        </p>
      </motion.div>
    </div>
  );
}
